import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import SyncIcon from '@mui/icons-material/Sync';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import BugReportIcon from '@mui/icons-material/BugReport';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Box, Button, Grid, Paper, Typography, Divider } from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import AdminPageWrapper from './AdminPageWrapper';
import { adminDashboardHelpItems } from '../../utils/helpItems';

const AdminDashboard = ({ darkMode, toggleDarkMode, themeVariant }) => {
    return (
        <AdminPageWrapper
            title="Admin Dashboard"
            helpItems={adminDashboardHelpItems}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Typography variant="h4" gutterBottom>
                Admin Dashboard
            </Typography>
            
            {/* Team Management Section */}
            <Box sx={{ my: 4 }} className="team-management-section">
                <Typography 
                    variant="h5" 
                    sx={{ 
                        mb: 2, 
                        pb: 1, 
                        fontWeight: 500,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        display: 'inline-block'
                    }}
                >
                    Team Management
                </Typography>
                
                <Grid container spacing={3}>
                    {/* User Management Card */}
                    <Grid item xs={12} md={6} lg={4} className="user-management-card">
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    User Management
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <PersonIcon sx={{ fontSize: 60, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    View and manage system users
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/users"
                                    variant="contained" 
                                    color='secondary'
                                    fullWidth
                                >
                                    Manage Users
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Assignment Management Card */}
                    <Grid item xs={12} md={6} lg={4} className="assignment-management-card">
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                justifyContent: 'space-between'
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Assignment Management
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <AssignmentIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
                                </Box>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Manage video assignments and track progress. Assign videos to team members and set due dates.
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/assignments"
                                    variant="contained" 
                                    color="secondary"
                                    fullWidth
                                >
                                    View Assignments
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Bug Reports Card */}
                    <Grid item xs={12} md={6} lg={4} className="bug-reports-card">
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'space-between'
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Bug Reports
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <BugReportIcon sx={{ fontSize: 60, color: 'error.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    View and manage user-submitted bug reports
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/bug-reports"
                                    variant="contained" 
                                    color="error"
                                    fullWidth
                                >
                                    View Bug Reports
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            
            {/* Admin Only Section */}
            <Box sx={{ my: 6, pt: 4 }} className="admin-tools-section">
                {/* Add a horizontal divider before the section */}
                <Divider sx={{ 
                    my: 4, 
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    borderWidth: 1
                }} />
                
                <Typography 
                    variant="h5" 
                    sx={{ 
                        mb: 2, 
                        pb: 1, 
                        fontWeight: 500,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        display: 'inline-block'
                    }}
                >
                    Admin Only
                </Typography>
                
                <Grid container spacing={3}>
                    {/* Files & Folders Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'space-between' 
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Files & Folders
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <FolderIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Browse and manage all files and folders in MongoDB
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/mongo-files"
                                    variant="contained" 
                                    fullWidth
                                >
                                    View Files & Folders
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Videos Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'space-between' 
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>   
                                <Typography variant="h6" gutterBottom>
                                    Videos
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <VideoLibraryIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Manage all video content and subtitles in MongoDB
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/mongo-videos"
                                    variant="contained" 
                                    color="secondary"
                                    fullWidth
                                >
                                    View Videos
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    
                    {/* Data Migration Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center', 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                justifyContent: 'space-between' 
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Data Migration
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <SyncIcon sx={{ fontSize: 60, color: 'success.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Migrate data between SQL and MongoDB storage systems
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/admin/migration"
                                    variant="contained" 
                                    color="success"
                                    fullWidth
                                >
                                    Migration Tools
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </AdminPageWrapper>
    );
};

export default AdminDashboard;